import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import MemberIDCard from "../components/MemberIDCard";
import {
  LayoutDashboard,
  Users,
  Network,
  Wallet,
  LogOut,
  Menu,
  X,
  Copy,
  Award,
  Plane,
  Star,
  ChevronRight,
  IndianRupee,
  AlertCircle,
  CheckCircle,
  Package,
  ShoppingBag,
  Camera,
  User,
  IdCard,
  Settings,
  FileText,
  Upload,
  CreditCard,
  Building2,
  Receipt,
  Lock,
  Eye,
  EyeOff,
  Brain,
  Cpu,
  Sparkles,
  Phone,
  MessageCircle
} from "lucide-react";

// Official Achraj Logo URL
const LOGO_URL = "/achraj_logo.png";

// Animated Grid Background Component
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
  </div>
);
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const NAV_ITEMS = [
  { path: "/member", label: "Dashboard", icon: LayoutDashboard },
  { path: "/member/team", label: "My Team", icon: Users },
  { path: "/network", label: "Network Tree", icon: Network },
  { path: "/member/income", label: "Income", icon: Wallet },
  { path: "/member/wallet", label: "My Wallet", icon: CreditCard },
  { path: "/member/orders", label: "My Orders", icon: Receipt },
  { path: "/member/kyc", label: "KYC & Bank", icon: FileText },
  { path: "/member/products", label: "Products", icon: ShoppingBag },
  { path: "/member/id-card", label: "My ID Card", icon: IdCard },
  { path: "/member/security", label: "Security", icon: Lock },
];

function Sidebar({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#0A0F1C] border-r border-cyan-500/10 z-50 transform transition-transform lg:translate-x-0
        ${open ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full blur opacity-50"></div>
                <img src={LOGO_URL} alt="Achraj Logo" className="relative h-10 w-10 rounded-full object-cover border-2 border-cyan-500/50" />
              </div>
              <div>
                <span className="font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Achraj AI</span>
                <p className="text-xs text-slate-500">Member Portal</p>
              </div>
            </Link>
            <button className="lg:hidden text-slate-400" onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="px-4 space-y-1">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Support Contact */}
        <div className="px-4 py-3 mx-4 mb-16 bg-slate-800/50 rounded-xl border border-slate-700/50">
          <p className="text-xs text-slate-500 mb-2">Need Help?</p>
          <a 
            href="tel:+919956864243" 
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-1"
          >
            <Phone className="w-4 h-4" /> +91 99568 64243
          </a>
          <a 
            href="https://wa.me/919956864243" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Support
          </a>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            data-testid="member-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}

// Dashboard Overview
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [repurchaseStatus, setRepurchaseStatus] = useState(null);
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const { user, token } = useAuth();

  // Calculate which month's payout to show
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth() + 1; // 1-12
  const currentYear = today.getFullYear();
  
  // Before 5th: Show previous month's payout
  // After 5th: Show current month's payout
  const isAfter5th = currentDay > 5;
  
  // Get previous month
  const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, payoutsRes, repurchaseRes, incomeRes, purchaseRes] = await Promise.all([
          axios.get(`${API}/network/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/payouts`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/member/repurchase-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/member/income-summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/member/purchase-summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
        ]);
        setStats(statsRes.data);
        setPayouts(payoutsRes.data || []);
        setRepurchaseStatus(repurchaseRes.data);
        setIncomeSummary(incomeRes.data);
        setPurchaseSummary(purchaseRes.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchData();
  }, [token]);

  // Find relevant payout based on date
  const getDisplayPayout = () => {
    if (!payouts || payouts.length === 0) return null;
    
    if (isAfter5th) {
      // After 5th: Show current month's payout
      return payouts.find(p => p.month === currentMonth && p.year === currentYear);
    } else {
      // Before 5th: Show previous month's payout (if paid)
      const prevPayout = payouts.find(p => p.month === prevMonth && p.year === prevYear);
      return prevPayout;
    }
  };
  
  const displayPayout = getDisplayPayout();
  const displayMonth = isAfter5th ? currentMonth : prevMonth;
  const displayYear = isAfter5th ? currentYear : prevYear;
  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const copyMemberId = () => {
    navigator.clipboard.writeText(user?.member_id || "");
    toast.success("Member ID copied!");
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.member_id}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const currentRankLevel = stats?.rank?.level || 1;
  const progress = (currentRankLevel / 20) * 100;

  return (
    <div className="space-y-6" data-testid="member-overview">
      {/* MONTHLY INCOME DISPLAY — hidden per business requirement (income not shown to members) */}

      {/* Credit Balance & Purchase Summary Card */}
      {purchaseSummary && (
        <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-cyan-500/30" data-testid="purchase-summary-card">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-cyan-500/20 rounded-xl">
                <CreditCard className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">Credit Balance</p>
                <p className="text-4xl font-bold text-white mt-1">
                  ₹{(purchaseSummary.credit_balance || 0).toLocaleString()}
                </p>
                <p className="text-slate-400 text-sm mt-1">Carry forward from previous purchases</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="bg-slate-900/50 rounded-xl p-4 text-center min-w-[140px]">
                <p className="text-slate-400 text-xs">Monthly Requirement</p>
                <p className="text-2xl font-bold text-[#D4AF37]">₹{(purchaseSummary.plan_amount || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-500">Plan {purchaseSummary.plan_type}</p>
              </div>
              
              {!purchaseSummary.repurchased_this_month && (
                <div className="bg-slate-900/50 rounded-xl p-4 text-center min-w-[140px]">
                  <p className="text-slate-400 text-xs">Still Needed</p>
                  <p className="text-2xl font-bold text-orange-400">₹{(purchaseSummary.remaining_for_repurchase || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">For this month</p>
                </div>
              )}
              
              {purchaseSummary.repurchased_this_month && (
                <div className="bg-green-500/20 rounded-xl p-4 text-center min-w-[140px] border border-green-500/30">
                  <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-1" />
                  <p className="text-green-400 font-semibold text-sm">Repurchase Done</p>
                  <p className="text-xs text-green-400/70">This month ✓</p>
                </div>
              )}
            </div>
          </div>
          
          {purchaseSummary.last_billing_date && (
            <p className="text-slate-500 text-xs mt-4 text-right">
              Last purchase: {new Date(purchaseSummary.last_billing_date).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric'
              })}
            </p>
          )}
        </div>
      )}

      {/* KYC Warning */}
      {repurchaseStatus && !repurchaseStatus.kyc_complete && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div>
            <p className="text-yellow-400 font-semibold">KYC Incomplete</p>
            <p className="text-yellow-400/70 text-sm">Please complete your KYC to receive payouts. Go to KYC & Bank section.</p>
          </div>
        </div>
      )}

      {/* Repurchase Warning - Only show after 5th day of month (closing extends to 5th) */}
      {repurchaseStatus && !repurchaseStatus.repurchased && new Date().getDate() > 5 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 flex items-center gap-3">
          <Package className="w-5 h-5 text-orange-400 flex-shrink-0" />
          <div>
            <p className="text-orange-400 font-semibold">Monthly Repurchase Pending</p>
            <p className="text-orange-400/70 text-sm">Complete your monthly purchase (₹{repurchaseStatus.plan_type === 'A' ? '4,000' : repurchaseStatus.plan_type === 'B' ? '2,000' : repurchaseStatus.plan_type === 'C' ? '1,000' : '500'}) to activate this month&apos;s payout.</p>
          </div>
        </div>
      )}

      {/* Grace Period Notice - Show from 1st to 5th */}
      {repurchaseStatus && !repurchaseStatus.repurchased && new Date().getDate() <= 5 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center gap-3">
          <Package className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <div>
            <p className="text-blue-400 font-semibold">Grace Period Active</p>
            <p className="text-blue-400/70 text-sm">You have until the 5th of this month to complete your repurchase for the previous month&apos;s payout.</p>
          </div>
        </div>
      )}

      {/* Payout Eligibility Warning */}
      {repurchaseStatus && repurchaseStatus.direct_joinings < 2 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <Users className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-semibold">Payout Inactive</p>
            <p className="text-red-400/70 text-sm">You need at least 2 active direct sponsors to activate payouts. Current: {repurchaseStatus.direct_joinings}</p>
          </div>
        </div>
      )}

      {/* Payout Active Badge */}
      {repurchaseStatus?.payout_eligible && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          <div>
            <p className="text-green-400 font-semibold">Payout Active ✓</p>
            <p className="text-green-400/70 text-sm">Your payout for this month is active. Keep growing your network!</p>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#B5952F]/10 rounded-2xl p-6 border border-[#D4AF37]/30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome, {user?.name}!</h1>
            <p className="text-slate-300">
              Member ID: <span className="text-[#D4AF37] font-mono font-bold">{user?.member_id}</span>
              <button onClick={copyMemberId} className="ml-2 text-slate-400 hover:text-[#D4AF37]">
                <Copy className="w-4 h-4 inline" />
              </button>
            </p>
          </div>
          <Button 
            onClick={copyReferralLink}
            data-testid="copy-referral-link-btn"
            className="bg-[#D4AF37] hover:bg-[#B5952F] text-black rounded-full"
          >
            <Copy className="w-4 h-4 mr-2" /> Copy Referral Link
          </Button>
        </div>
      </div>

      {/* Status Alerts */}
      {!user?.is_active && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-300">Your account is not active. Please contact admin after payment.</p>
        </div>
      )}

      {user?.is_active && !user?.is_payout_active && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400" />
          <p className="text-yellow-300">Complete 2 joinings within 45 days to activate payouts. Current: {stats?.direct_sponsors || 0}/2</p>
        </div>
      )}

      {user?.is_payout_active && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <p className="text-green-300">Payout active! Qualified up to Level {stats?.qualified_level || 0}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-900/50 border-slate-800 hover:border-[#D4AF37]/30 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Direct Sponsors</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.direct_sponsors || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800 hover:border-[#D4AF37]/30 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">AI Team Total</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.total_ai_team || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-green-500/10">
                <Network className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800 hover:border-[#D4AF37]/30 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Plan Type</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.plan_type || user?.plan_type || "N/A"}</p>
                <p className="text-xs text-slate-500">Up to Level {stats?.max_level || 17}</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Star className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rank & Level Info */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#D4AF37]" /> Your Rank & Level
            </h3>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.rank?.title || "One Star Leader"}</p>
                <p className="text-slate-400">Level {stats?.current_level || 1} | Qualified: Level {stats?.qualified_level || 0}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Level Progress</span>
                <span className="text-[#D4AF37]">{progress.toFixed(0)}%</span>
              </div>
              <Progress value={progress} className="h-2 bg-slate-800" />
            </div>

            {stats?.rank?.trip && (
              <div className="mt-4 p-4 bg-[#2E86AB]/10 rounded-xl border border-[#2E86AB]/30">
                <div className="flex items-center gap-3">
                  <Plane className="w-5 h-5 text-[#2E86AB]" />
                  <div>
                    <p className="text-white font-medium">{stats?.rank?.trip}</p>
                    <p className="text-sm text-slate-400">Trip Budget: ₹{stats?.rank?.budget?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Level-wise Team */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">AI Team by Level</h3>
            {stats?.level_wise_team && Object.keys(stats.level_wise_team).length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(stats.level_wise_team).map(([level, data]) => (
                  <div key={level} className="flex justify-between items-center p-2 bg-slate-800/50 rounded">
                    <span className="text-slate-300">Level {level}</span>
                    <Badge className="bg-[#D4AF37]/20 text-[#D4AF37]">
                      {typeof data === 'object' ? data.filled : data} members
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No team members yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Payouts */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Recent Payouts</h3>
            <Link to="/member/income" className="text-[#D4AF37] text-sm hover:underline flex items-center">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {payouts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Period</TableHead>
                  <TableHead className="text-slate-400">AI Team</TableHead>
                  <TableHead className="text-slate-400">Net Amount</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((p) => (
                  <TableRow key={p.id} className="border-slate-800">
                    <TableCell className="text-white">{p.month}/{p.year}</TableCell>
                    <TableCell className="text-slate-300">{p.ai_team_count}</TableCell>
                    <TableCell className="text-[#D4AF37] font-bold">₹{p.net_amount?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={p.status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                        {p.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-slate-400 text-center py-8">No payouts yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// My Team
function MyTeam() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberTeam, setMemberTeam] = useState(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const fetchTeam = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/network/downline`, { headers: { Authorization: `Bearer ${token}` } });
        setTeam(res.data);
      } catch (err) {
        console.error("Error:", err);
      }
      setLoading(false);
    };
    fetchTeam();
  }, [token]);

  const viewMemberTeam = async (member) => {
    setSelectedMember(member);
    setTeamModalOpen(true);
    setLoadingTeam(true);
    try {
      const res = await axios.get(`${API}/network/member/${member.member_id}/team`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setMemberTeam(res.data);
    } catch (err) {
      console.error("Error fetching member team:", err);
      toast.error("Failed to load member's team");
    }
    setLoadingTeam(false);
  };

  return (
    <div data-testid="member-team" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-white">My Sponsored Team</h1>
        <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 px-3 py-1 w-fit">
          {team.length} Members
        </Badge>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="relative inline-block">
            <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
          </div>
          <p className="text-slate-400 mt-4">Loading team...</p>
        </div>
      ) : team.length > 0 ? (
        <>
          {/* Mobile Scroll Indicator */}
          <div className="flex items-center gap-2 text-xs text-slate-400 sm:hidden bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700/50">
            <ChevronRight className="w-4 h-4 animate-pulse" />
            <span>Swipe left to see more details</span>
          </div>

          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {team.map((member) => (
              <Card key={member.id} className="bg-slate-900/50 border-slate-700/50">
                <CardContent className="p-4">
                  {/* Member Info Row */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {member.name?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{member.name}</p>
                      <p className="text-cyan-400 text-sm font-mono">{member.member_id}</p>
                    </div>
                    <Badge className={member.is_active ? "bg-green-500/20 text-green-400 border-green-500/30 text-xs" : "bg-red-500/20 text-red-400 border-red-500/30 text-xs"}>
                      {member.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <p className="text-slate-500 text-xs">Phone</p>
                      <p className="text-slate-300 truncate">{member.phone || "N/A"}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <p className="text-slate-500 text-xs">Plan</p>
                      <p className="text-purple-400 font-semibold">{member.plan_type}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <p className="text-slate-500 text-xs">Joined</p>
                      <p className="text-slate-300">{new Date(member.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <p className="text-slate-500 text-xs">Team Size</p>
                      <p className="text-cyan-400 font-semibold">{member.direct_sponsors_count || 0}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewMemberTeam(member)}
                      className="flex-1 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
                    >
                      <Users className="w-4 h-4 mr-1" /> View Team
                    </Button>
                    {member.phone && (
                      <>
                        <a 
                          href={`tel:+91${member.phone}`}
                          className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg text-cyan-400 transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                        <a 
                          href={`https://wa.me/91${member.phone}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="bg-slate-900/50 border-slate-700/50 hidden sm:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">Member</TableHead>
                      <TableHead className="text-slate-400">Phone</TableHead>
                      <TableHead className="text-slate-400">Plan</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Joined</TableHead>
                      <TableHead className="text-slate-400 text-center">Team</TableHead>
                      <TableHead className="text-slate-400 text-right">Contact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.map((member) => (
                      <TableRow key={member.id} className="border-slate-700/50 hover:bg-slate-800/30">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold">
                              {member.name?.charAt(0) || "?"}
                            </div>
                            <div>
                              <p className="text-white font-medium">{member.name}</p>
                              <p className="text-cyan-400 text-sm font-mono">{member.member_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-slate-300">{member.phone || "N/A"}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">{member.plan_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={member.is_active ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                            {member.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(member.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewMemberTeam(member)}
                            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500/50"
                          >
                            <Users className="w-4 h-4 mr-1" />
                            View ({member.direct_sponsors_count || 0})
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {member.phone && (
                              <>
                                <a 
                                  href={`tel:+91${member.phone}`}
                                  className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg text-cyan-400 transition-colors"
                                  title="Call"
                                >
                                  <Phone className="w-4 h-4" />
                                </a>
                                <a 
                                  href={`https://wa.me/91${member.phone}?text=Hi%20${encodeURIComponent(member.name)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition-colors"
                                  title="WhatsApp"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </a>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-slate-900/50 border-slate-700/50">
          <CardContent className="p-8 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-slate-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">No team members yet</h3>
            <p className="text-slate-400 text-sm sm:text-base mb-4">Share your Member ID to start building your network</p>
          </CardContent>
        </Card>
      )}

      {/* Team Stats - Responsive Grid */}
      {team.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-cyan-400">{team.length}</p>
              <p className="text-slate-400 text-xs sm:text-sm">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-green-400">{team.filter(m => m.is_active).length}</p>
              <p className="text-slate-400 text-xs sm:text-sm">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-red-400">{team.filter(m => !m.is_active).length}</p>
              <p className="text-slate-400 text-xs sm:text-sm">Inactive</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Member Team Modal - Mobile Optimized */}
      <Dialog open={teamModalOpen} onOpenChange={setTeamModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
              <Users className="w-5 h-5 text-cyan-400" />
              <span className="truncate">{selectedMember?.name}'s Team</span>
            </DialogTitle>
          </DialogHeader>
          
          {loadingTeam ? (
            <div className="text-center py-8">
              <div className="w-10 h-10 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-400 mt-4">Loading team...</p>
            </div>
          ) : memberTeam?.team?.length > 0 ? (
            <div className="space-y-4">
              {/* Member Info Header */}
              <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4 border border-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold">
                    {memberTeam.member?.name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{memberTeam.member?.name}</p>
                    <p className="text-cyan-400 text-sm font-mono">{memberTeam.member?.member_id}</p>
                  </div>
                  <Badge className={memberTeam.member?.is_active ? "bg-green-500/20 text-green-400 text-xs" : "bg-red-500/20 text-red-400 text-xs"}>
                    {memberTeam.member?.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>

              {/* Mobile Team Cards */}
              <div className="sm:hidden space-y-3">
                {memberTeam.team.map((m) => (
                  <div key={m.id} className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                        {m.name?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{m.name}</p>
                        <p className="text-cyan-400 text-xs font-mono">{m.member_id}</p>
                      </div>
                      <Badge className={m.is_active ? "bg-green-500/20 text-green-400 text-xs" : "bg-red-500/20 text-red-400 text-xs"}>
                        {m.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Phone: <span className="text-slate-300">{m.phone || "N/A"}</span></span>
                      <span className="text-slate-400">Team: <span className="text-purple-400 font-semibold">{m.direct_sponsors_count || 0}</span></span>
                    </div>
                    {m.phone && (
                      <div className="flex gap-2 mt-2">
                        <a 
                          href={`tel:+91${m.phone}`}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-cyan-500/20 rounded text-cyan-400 text-xs"
                        >
                          <Phone className="w-3 h-3" /> Call
                        </a>
                        <a 
                          href={`https://wa.me/91${m.phone}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-green-500/20 rounded text-green-400 text-xs"
                        >
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop Team Table */}
              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Member</TableHead>
                      <TableHead className="text-slate-400">Phone</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Their Team</TableHead>
                      <TableHead className="text-slate-400 text-right">Contact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberTeam.team.map((m) => (
                      <TableRow key={m.id} className="border-slate-700/50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                              {m.name?.charAt(0) || "?"}
                            </div>
                            <div>
                              <p className="text-white text-sm">{m.name}</p>
                              <p className="text-cyan-400 text-xs font-mono">{m.member_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">{m.phone || "N/A"}</TableCell>
                        <TableCell>
                          <Badge className={m.is_active ? "bg-green-500/20 text-green-400 text-xs" : "bg-red-500/20 text-red-400 text-xs"}>
                            {m.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-purple-400 font-semibold">{m.direct_sponsors_count || 0}</span>
                          <span className="text-slate-500 text-sm"> members</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {m.phone && (
                              <>
                                <a 
                                  href={`tel:+91${m.phone}`}
                                  className="p-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 rounded text-cyan-400 transition-colors"
                                >
                                  <Phone className="w-3 h-3" />
                                </a>
                                <a 
                                  href={`https://wa.me/91${m.phone}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded text-green-400 transition-colors"
                                >
                                  <MessageCircle className="w-3 h-3" />
                                </a>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 flex items-center justify-between">
                <span className="text-slate-400 text-sm">Total Direct Sponsors:</span>
                <span className="text-cyan-400 font-bold text-lg">{memberTeam.team.length}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No direct sponsors under this member</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Income Details
function WalletSection() {
  const { token } = useAuth();
  const [data, setData] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/member/wallet`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch (err) {
        toast.error("Failed to load wallet");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  return (
    <div className="space-y-6" data-testid="member-wallet">
      <h1 className="text-2xl font-bold text-white">My Wallet</h1>

      <Card className="bg-gradient-to-br from-[#D4AF37]/20 to-slate-900 border-[#D4AF37]/40">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm">Available Credits (1 credit = ₹1)</p>
            <p className="text-4xl font-bold text-[#D4AF37] mt-1" data-testid="wallet-balance">
              {loading ? "…" : `₹${(data.balance || 0).toLocaleString()}`}
            </p>
            <p className="text-slate-400 text-xs mt-1">Use to shop on the website or at any franchise</p>
          </div>
          <CreditCard className="w-14 h-14 text-[#D4AF37]/60" />
        </CardContent>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader><CardTitle className="text-white text-lg">Transaction History</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-400 text-center py-6">Loading...</p>
          ) : (data.transactions || []).length === 0 ? (
            <p className="text-slate-400 text-center py-6" data-testid="wallet-no-txns">No transactions yet.</p>
          ) : (
            <div className="space-y-2">
              {data.transactions.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-800/60 rounded-lg" data-testid={`wallet-txn-${i}`}>
                  <div>
                    <p className="text-white text-sm">{t.reason}</p>
                    <p className="text-slate-500 text-xs">{(t.created_at || "").split("T")[0]}{t.reference ? ` • Ref: ${t.reference}` : ""}</p>
                  </div>
                  <span className={`font-bold ${t.type === "credit" ? "text-green-400" : "text-red-400"}`}>
                    {t.type === "credit" ? "+" : "-"}₹{(t.amount || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function IncomeDetails() {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [incomeData, setIncomeData] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [payoutsRes, summaryRes, incomeRes, statsRes] = await Promise.all([
          axios.get(`${API}/payouts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/payouts/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { total: 0, pending: 0, paid: 0 } })),
          axios.get(`${API}/member/income`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/company/stats`).catch(() => ({ data: { total_ids: 0 } }))
        ]);
        setPayouts(payoutsRes.data);
        setSummary(summaryRes.data);
        setIncomeData(incomeRes.data);
        setCompanyStats(statsRes.data);
      } catch (err) {
        console.error("Error:", err);
        toast.error("Failed to load income data");
      }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="member-income-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div data-testid="member-income">
      <h1 className="text-2xl font-bold text-white mb-6">AI Tree Income Details</h1>

      {/* Key Info Cards - Entry Level, Company Level, Payable Levels */}
      {incomeData && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <p className="text-slate-400 text-xs">Entry Level</p>
              <p className="text-2xl font-bold text-[#D4AF37]" data-testid="entry-level">{incomeData.entry_level ?? 0}</p>
              <p className="text-xs text-slate-500">Fixed at joining</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <p className="text-slate-400 text-xs">Company Level</p>
              <p className="text-2xl font-bold text-blue-400" data-testid="company-level">{incomeData.company_level ?? 0}</p>
              <p className="text-xs text-slate-500">{companyStats?.total_ids || incomeData.total_ai_team || 0} total IDs</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="p-4">
              <p className="text-slate-400 text-xs">Payable Levels</p>
              <p className="text-2xl font-bold text-green-400" data-testid="payable-levels">{incomeData.payable_levels ?? 0}</p>
              <p className="text-xs text-slate-500">Company - Entry</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <p className="text-slate-400 text-xs">Unlocked Level</p>
              <p className="text-2xl font-bold text-purple-400" data-testid="unlocked-level">{incomeData.unlocked_level ?? 10}</p>
              <p className="text-xs text-slate-500">{incomeData.direct_joinings || 0} direct</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <p className="text-slate-400 text-xs">Plan Max Level</p>
              <p className="text-2xl font-bold text-[#E65100]" data-testid="plan-max-level">{incomeData.plan_max_level ?? 17}</p>
              <p className="text-xs text-slate-500">Plan {incomeData.plan_type || 'D'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Income Summary Cards (Gross/Service/Net/Payout) — hidden per business requirement */}

      {/* Payout Eligibility Status */}
      {incomeData && !incomeData.payout_eligible && (
        <Card className="bg-red-500/10 border border-red-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-300 font-semibold">Payout Not Active</p>
                <p className="text-red-200/70 text-sm">
                  {incomeData.direct_joinings < 2 && `Need ${2 - incomeData.direct_joinings} more direct sponsors. `}
                  {!incomeData.repurchased_this_month && "Monthly repurchase pending."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Income Status Message */}
      {incomeData && incomeData.status !== "active" && incomeData.status !== "payout_pending" && (
        <Card className="bg-yellow-500/10 border border-yellow-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-yellow-300 font-semibold">
                  {incomeData.status === "no_payable_levels" && "No Payable Levels Yet"}
                  {incomeData.status === "inactive" && "Account Not Active"}
                  {incomeData.status === "no_position" && "No Tree Position Assigned"}
                </p>
                <p className="text-yellow-200/70 text-sm">{incomeData.message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Tree Level Breakdown Table */}
      {incomeData?.levels && incomeData.levels.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-2">AI Tree Team Breakdown</h3>
            <p className="text-sm text-slate-400 mb-4">
              Your AI matrix team placement across levels.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Your Level</TableHead>
                    <TableHead className="text-slate-400">Co. Level Req.</TableHead>
                    <TableHead className="text-slate-400">Matrix Slots</TableHead>
                    <TableHead className="text-slate-400">AI Team</TableHead>
                    <TableHead className="text-slate-400">Plan Breakdown</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incomeData.levels.map((level) => (
                    <TableRow 
                      key={level.level} 
                      className={`border-slate-800 ${!level.is_payable ? 'opacity-60' : ''}`}
                      data-testid={`income-level-${level.level}`}
                    >
                      <TableCell className="text-white font-bold">Level {level.level}</TableCell>
                      <TableCell className="text-slate-300">{level.company_level_required}</TableCell>
                      <TableCell className="text-slate-300 font-mono text-xs">
                        {level.start_pos} - {level.end_pos}
                      </TableCell>
                      <TableCell>
                        <span className="text-green-400 font-semibold">{level.ai_team_size}</span>
                        <span className="text-slate-500">/{level.potential}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {level.plan_breakdown && (
                          <div className="flex gap-1 flex-wrap">
                            {level.plan_breakdown.A > 0 && <span className="bg-purple-500/20 text-purple-400 px-1 rounded">A:{level.plan_breakdown.A}</span>}
                            {level.plan_breakdown.B > 0 && <span className="bg-blue-500/20 text-blue-400 px-1 rounded">B:{level.plan_breakdown.B}</span>}
                            {level.plan_breakdown.C > 0 && <span className="bg-green-500/20 text-green-400 px-1 rounded">C:{level.plan_breakdown.C}</span>}
                            {level.plan_breakdown.D > 0 && <span className="bg-yellow-500/20 text-yellow-400 px-1 rounded">D:{level.plan_breakdown.D}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {level.status === "active" && level.is_payable ? (
                          <Badge className="bg-green-500/20 text-green-400">✓ Earning</Badge>
                        ) : level.status === "locked" ? (
                          <Badge className="bg-red-500/20 text-red-400">🔒 {level.status_message}</Badge>
                        ) : level.status === "plan_limit" ? (
                          <Badge className="bg-orange-500/20 text-orange-400">📊 {level.status_message}</Badge>
                        ) : (
                          <Badge className="bg-blue-500/20 text-blue-400">⏳ {level.status_message}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Level Unlock Rules Info */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Level Unlock Rules</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-green-400 font-semibold mb-2">✓ Levels 1-10: Auto-Unlock</p>
              <p className="text-slate-300">Automatically unlock as company grows</p>
              <p className="text-slate-400 text-xs mt-1">No direct sponsor requirement</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-[#E65100] font-semibold mb-2">🔒 Levels 11-20: Direct Sponsors Required</p>
              <p className="text-slate-300">4 direct sponsors per level</p>
              <p className="text-slate-400 text-xs mt-1">
                L11: 4 | L12: 8 | L13: 12 | L14: 16 | L15: 20 | L16: 24 | L17: 28 | L18: 32 | L19: 36 | L20: 40
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payout History Summary & Table — hidden per business requirement (income not shown) */}
    </div>
  );
}

// Products (for viewing)
function ProductsView() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="member-products">
      <h1 className="text-2xl font-bold text-white mb-6">Subscription Products</h1>
      <p className="text-slate-400 mb-6">These products are included in your monthly subscription.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.map((p) => (
          <Card key={p.id} className="bg-slate-900/50 border-slate-800 overflow-hidden">
            {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-40 object-cover" />}
            <CardContent className="p-4">
              <Badge className="mb-2">{p.category_name}</Badge>
              <h3 className="text-white font-semibold">{p.name}</h3>
              <p className="text-slate-400 text-sm mt-1 line-clamp-2">{p.description}</p>
              <p className="text-[#D4AF37] font-bold mt-2">₹{p.price}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// My Orders Component - Shows both Online Orders and Offline Billings
function MyOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [billings, setBillings] = useState([]);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('billings');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, billingsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/member/orders`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/member/billings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/member/purchase-summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setOrders(ordersRes.data || []);
      setBillings(billingsRes.data || []);
      setPurchaseSummary(summaryRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getOrderStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      confirmed: "bg-blue-500/20 text-blue-400",
      processing: "bg-purple-500/20 text-purple-400",
      shipped: "bg-indigo-500/20 text-indigo-400",
      delivered: "bg-green-500/20 text-green-400",
      cancelled: "bg-red-500/20 text-red-400"
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading purchase history...</div>
      </div>
    );
  }

  return (
    <div data-testid="my-orders" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">My Orders & Purchases</h1>
        <p className="text-slate-400 text-sm">Track your purchase history, billings, and credit balance</p>
      </div>

      {/* Credit Balance Summary Card */}
      {purchaseSummary && (
        <Card className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 border-cyan-500/30">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-500/20 rounded-xl">
                  <CreditCard className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Credit Balance (Carry Forward)</p>
                  <p className="text-3xl font-bold text-white">₹{(purchaseSummary.credit_balance || 0).toLocaleString()}</p>
                </div>
              </div>
              
              <div className="flex gap-3 flex-wrap">
                <div className="bg-slate-900/50 rounded-lg px-4 py-2 text-center">
                  <p className="text-slate-500 text-xs">Plan {purchaseSummary.plan_type}</p>
                  <p className="text-[#D4AF37] font-semibold">₹{purchaseSummary.plan_amount?.toLocaleString()}/month</p>
                </div>
                
                {purchaseSummary.repurchased_this_month ? (
                  <div className="bg-green-500/20 rounded-lg px-4 py-2 text-center border border-green-500/30">
                    <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                    <p className="text-green-400 text-xs font-semibold">Repurchase Done</p>
                  </div>
                ) : (
                  <div className="bg-orange-500/20 rounded-lg px-4 py-2 text-center border border-orange-500/30">
                    <p className="text-orange-400 text-xs">Still Needed</p>
                    <p className="text-orange-400 font-semibold">₹{purchaseSummary.remaining_for_repurchase?.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
            
            {purchaseSummary.credit_balance > 0 && (
              <p className="text-cyan-400/70 text-sm mt-3 bg-cyan-500/10 rounded-lg p-2">
                💡 Your credit balance of ₹{purchaseSummary.credit_balance.toLocaleString()} will be automatically applied to your next purchase.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs for Billings and Orders */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        <Button
          variant={activeTab === 'billings' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('billings')}
          className={activeTab === 'billings' ? 'bg-[#D4AF37] text-black' : 'text-slate-400'}
          data-testid="billings-tab"
        >
          <Receipt className="w-4 h-4 mr-2" />
          Office Billings ({billings.length})
        </Button>
        <Button
          variant={activeTab === 'orders' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('orders')}
          className={activeTab === 'orders' ? 'bg-[#D4AF37] text-black' : 'text-slate-400'}
          data-testid="orders-tab"
        >
          <ShoppingBag className="w-4 h-4 mr-2" />
          Online Orders ({orders.length})
        </Button>
      </div>

      {/* Billings Tab */}
      {activeTab === 'billings' && (
        <div className="space-y-4">
          {billings.length === 0 ? (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8 text-center">
                <Receipt className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No billings yet</p>
                <p className="text-slate-500 text-sm">Purchases made from office/franchise will appear here</p>
              </CardContent>
            </Card>
          ) : (
            billings.map((bill) => (
              <Card key={bill.billing_id} className="bg-slate-900/50 border-slate-800" data-testid={`billing-${bill.billing_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[#D4AF37] font-mono font-bold">{bill.billing_id}</p>
                      <p className="text-slate-400 text-sm">
                        {new Date(bill.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500/20 text-blue-400">{bill.billing_type || 'Office'}</Badge>
                      {bill.repurchase_updated && (
                        <Badge className="bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Repurchase
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-slate-800 pt-3">
                    <p className="text-slate-400 text-sm mb-2">Items:</p>
                    <div className="space-y-2">
                      {bill.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white">
                            {item.product_name} × {item.quantity}
                            {item.point_value > 0 && <span className="text-purple-400 text-xs ml-2">{(item.point_value || 0) * (item.quantity || 0)} PV</span>}
                          </span>
                          <span className="text-[#D4AF37]">₹{(item.total || item.unit_price * item.quantity).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-slate-800 mt-3 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Bill Amount:</span>
                      <span className="text-white font-semibold">₹{bill.total_amount?.toLocaleString()}</span>
                    </div>

                    {bill.total_pv > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-purple-400">Total Point Value:</span>
                        <span className="text-purple-400 font-semibold">{bill.total_pv} PV</span>
                      </div>
                    )}
                    
                    {bill.credit_used > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-cyan-400">Credit Used:</span>
                        <span className="text-cyan-400">-₹{bill.credit_used?.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {bill.extra_amount > 0 && (
                      <div className="flex justify-between text-sm bg-cyan-500/10 rounded p-2">
                        <span className="text-cyan-400">
                          <CreditCard className="w-4 h-4 inline mr-1" />
                          Carry Forward:
                        </span>
                        <span className="text-cyan-400 font-semibold">+₹{bill.extra_amount?.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {bill.credit_accumulated > 0 && !bill.repurchase_updated && (
                      <div className="flex justify-between text-sm bg-blue-500/10 rounded p-2">
                        <span className="text-blue-400">
                          <CreditCard className="w-4 h-4 inline mr-1" />
                          Credit Accumulated:
                        </span>
                        <span className="text-blue-400 font-semibold">₹{bill.credit_accumulated?.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <span>Payment: {bill.payment_mode?.toUpperCase()}</span>
                    {bill.payment_reference && <span>Ref: {bill.payment_reference}</span>}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8 text-center">
                <ShoppingBag className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No online orders yet</p>
                <p className="text-slate-500 text-sm">Your online orders will appear here</p>
              </CardContent>
            </Card>
          ) : (
            orders.map((order) => (
              <Card key={order.order_id} className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[#D4AF37] font-mono font-bold">Order #{order.order_id}</p>
                      <p className="text-slate-400 text-sm">
                        {new Date(order.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <Badge className={getOrderStatusBadge(order.order_status)}>
                      {order.order_status?.toUpperCase() || 'PENDING'}
                    </Badge>
                  </div>

                  <div className="border-t border-slate-800 pt-3">
                    <p className="text-slate-400 text-sm mb-2">Items:</p>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => {
                        const qty = item.quantity || 0;
                        const price = item.price ?? item.unit_price ?? 0;
                        return (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white">{item.name || item.product_name || 'Item'} × {qty}</span>
                          <span className="text-[#D4AF37]">₹{(price * qty).toLocaleString()}</span>
                        </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-slate-800 mt-3 pt-3 flex justify-between">
                    <span className="text-slate-400">Total Amount:</span>
                    <span className="text-white font-bold text-lg">₹{order.total_amount?.toLocaleString()}</span>
                  </div>

                  {order.admin_notes && (
                    <div className="mt-3 p-2 bg-slate-800/50 rounded text-sm">
                      <span className="text-slate-400">Note: </span>
                      <span className="text-slate-300">{order.admin_notes}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// KYC Section Component
function KYCSection() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kyc, setKyc] = useState({
    // Bank Details
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',
    // UPI
    upi_id: '',
    // Documents
    aadhaar_front_url: '',
    aadhaar_back_url: '',
    pan_card_url: '',
    // Nominee
    nominee_name: '',
    nominee_relation: '',
    nominee_aadhaar: '',
    nominee_phone: '',
    // Status
    kyc_status: 'pending'
  });

  useEffect(() => {
    fetchKYC();
  }, []);

  const fetchKYC = async () => {
    try {
      const res = await axios.get(`${API}/member/kyc`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKyc(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error("Error fetching KYC:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setKyc(prev => ({ ...prev, [fieldName]: reader.result }));
      toast.success("Document uploaded. Click 'Save KYC Details' to submit.");
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/member/kyc`, kyc, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("KYC details saved successfully!");
      fetchKYC();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save KYC details");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      submitted: "bg-blue-500/20 text-blue-400",
      verified: "bg-green-500/20 text-green-400",
      rejected: "bg-red-500/20 text-red-400"
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading KYC details...</div>
      </div>
    );
  }

  return (
    <div data-testid="kyc-section" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">KYC & Bank Details</h1>
          <p className="text-slate-400 text-sm">Complete your KYC for payouts</p>
        </div>
        <Badge className={getStatusBadge(kyc.kyc_status)}>
          {kyc.kyc_status?.toUpperCase() || 'PENDING'}
        </Badge>
      </div>

      {kyc.kyc_remarks && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 text-sm"><strong>Admin Remarks:</strong> {kyc.kyc_remarks}</p>
        </div>
      )}

      {/* Bank Details Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Bank Account Details</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Bank Name</Label>
              <Input
                value={kyc.bank_name || ''}
                onChange={(e) => setKyc({...kyc, bank_name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="e.g., State Bank of India"
              />
            </div>
            <div>
              <Label className="text-slate-400">Account Holder Name</Label>
              <Input
                value={kyc.account_holder_name || ''}
                onChange={(e) => setKyc({...kyc, account_holder_name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="Name as per bank account"
              />
            </div>
            <div>
              <Label className="text-slate-400">Account Number</Label>
              <Input
                value={kyc.account_number || ''}
                onChange={(e) => setKyc({...kyc, account_number: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="Enter account number"
              />
            </div>
            <div>
              <Label className="text-slate-400">IFSC Code</Label>
              <Input
                value={kyc.ifsc_code || ''}
                onChange={(e) => setKyc({...kyc, ifsc_code: e.target.value.toUpperCase()})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="e.g., SBIN0001234"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-400">Branch Name</Label>
              <Input
                value={kyc.branch_name || ''}
                onChange={(e) => setKyc({...kyc, branch_name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="e.g., Main Branch, Mumbai"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* UPI Details Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">UPI Details</h2>
          </div>
          <div>
            <Label className="text-slate-400">UPI ID</Label>
            <Input
              value={kyc.upi_id || ''}
              onChange={(e) => setKyc({...kyc, upi_id: e.target.value})}
              className="bg-slate-800 border-slate-700 text-white mt-1"
              placeholder="e.g., yourname@upi or 9876543210@paytm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Document Upload Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Identity Documents</h2>
          </div>
          <p className="text-slate-400 text-sm mb-4">Upload clear photos of your documents (Max 2MB each)</p>
          
          <div className="grid sm:grid-cols-3 gap-4">
            {/* Aadhaar Front */}
            <div className="space-y-2">
              <Label className="text-slate-400">Aadhaar Card (Front)</Label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-[#E65100] transition-colors">
                {kyc.aadhaar_front_url ? (
                  <div className="space-y-2">
                    <img src={kyc.aadhaar_front_url} alt="Aadhaar Front" className="w-full h-32 object-contain rounded" />
                    <p className="text-green-400 text-xs flex items-center justify-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Uploaded
                    </p>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Click to upload</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'aadhaar_front_url')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ position: 'relative' }}
                />
              </div>
            </div>

            {/* Aadhaar Back */}
            <div className="space-y-2">
              <Label className="text-slate-400">Aadhaar Card (Back)</Label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-[#E65100] transition-colors">
                {kyc.aadhaar_back_url ? (
                  <div className="space-y-2">
                    <img src={kyc.aadhaar_back_url} alt="Aadhaar Back" className="w-full h-32 object-contain rounded" />
                    <p className="text-green-400 text-xs flex items-center justify-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Uploaded
                    </p>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Click to upload</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'aadhaar_back_url')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ position: 'relative' }}
                />
              </div>
            </div>

            {/* PAN Card */}
            <div className="space-y-2">
              <Label className="text-slate-400">PAN Card</Label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-[#E65100] transition-colors">
                {kyc.pan_card_url ? (
                  <div className="space-y-2">
                    <img src={kyc.pan_card_url} alt="PAN Card" className="w-full h-32 object-contain rounded" />
                    <p className="text-green-400 text-xs flex items-center justify-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Uploaded
                    </p>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Click to upload</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'pan_card_url')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ position: 'relative' }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Nominee Details Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Nominee Details</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Nominee Name</Label>
              <Input
                value={kyc.nominee_name || ''}
                onChange={(e) => setKyc({...kyc, nominee_name: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="Full name of nominee"
              />
            </div>
            <div>
              <Label className="text-slate-400">Relationship</Label>
              <Input
                value={kyc.nominee_relation || ''}
                onChange={(e) => setKyc({...kyc, nominee_relation: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="e.g., Father, Mother, Spouse"
              />
            </div>
            <div>
              <Label className="text-slate-400">Nominee Aadhaar Number</Label>
              <Input
                value={kyc.nominee_aadhaar || ''}
                onChange={(e) => setKyc({...kyc, nominee_aadhaar: e.target.value.replace(/\D/g, '').slice(0, 12)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="12-digit Aadhaar number"
                maxLength={12}
              />
            </div>
            <div>
              <Label className="text-slate-400">Nominee Phone</Label>
              <Input
                value={kyc.nominee_phone || ''}
                onChange={(e) => setKyc({...kyc, nominee_phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                placeholder="10-digit mobile number"
                maxLength={10}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-[#E65100] to-[#FF8F00] hover:from-[#D84315] hover:to-[#E65100] text-white px-8"
        >
          {saving ? "Saving..." : "Save KYC Details"}
        </Button>
      </div>
    </div>
  );
}

// Security Section - Password Change
function SecuritySection() {
  const { token, user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changing, setChanging] = useState(false);

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 4) {
      toast.error("New password must be at least 4 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (currentPassword === newPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    setChanging(true);
    try {
      await axios.put(`${API}/member/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Password changed successfully!");
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChanging(false);
    }
  };

  return (
    <div data-testid="security-section" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Security Settings</h1>
        <p className="text-slate-400 text-sm">Manage your account password and security</p>
      </div>

      <Card className="bg-slate-900/50 border-slate-800 max-w-xl">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-blue-400 text-sm">
              <strong>Member ID:</strong> {user?.member_id}
            </p>
            <p className="text-blue-400/70 text-xs mt-1">
              Your current password is the last 4 digits of your phone number (or a custom password if changed).
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-400">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-400">New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  placeholder="Enter new password (min 4 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-400">Confirm New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={changing}
            className="w-full bg-gradient-to-r from-[#E65100] to-[#FF8F00] hover:from-[#D84315] hover:to-[#E65100] text-white"
          >
            {changing ? "Changing..." : "Change Password"}
          </Button>

          <p className="text-slate-500 text-xs text-center">
            Forgot your password? Contact admin to reset it.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// My ID Card Section
function MyIDCard() {
  const { user } = useAuth();
  
  // Prepare member data for ID card
  const memberData = {
    member_id: user?.member_id || '',
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    plan_type: user?.plan_type || 'C',
    sponsor_id: user?.sponsor_id || 'SELF',
    photo_url: user?.photo_url || ''
  };

  return (
    <div data-testid="member-id-card" className="space-y-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-white">My ID Card</h1>
        <p className="text-slate-400 text-sm">Download your official Achraj Member ID Card</p>
      </div>
      
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <MemberIDCard member={memberData} showCloseButton={false} />
        </CardContent>
      </Card>
      
      <Card className="bg-[#D4AF37]/10 border-[#D4AF37]/30">
        <CardContent className="p-4">
          <p className="text-[#D4AF37] text-sm text-center">
            <strong>Note:</strong> Your ID card is generated based on your profile information. 
            Keep it safe and use it for identification purposes within the Achraj network.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Member Dashboard
export default function MemberDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user, token, setUser } = useAuth();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoPreview) return;
    
    setUploading(true);
    try {
      await axios.put(`${API}/member/profile`, { photo_url: photoPreview }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local user state
      setUser({ ...user, photo_url: photoPreview });
      toast.success("Photo updated successfully!");
      setPhotoDialogOpen(false);
      setPhotoPreview(null);
    } catch (err) {
      toast.error("Failed to update photo");
    }
    setUploading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]" data-testid="member-dashboard">
      <GridBackground />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64 relative">
        <header className="sticky top-0 z-30 bg-[#0A0F1C]/80 backdrop-blur-xl border-b border-cyan-500/10 px-4 py-4">
          <div className="flex items-center justify-between">
            <button className="lg:hidden p-2 text-slate-400 hover:text-white" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-4 ml-auto">
              <div className="text-right">
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-xs bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent font-semibold">{user?.member_id}</p>
              </div>
              
              {/* Profile Photo with Edit */}
              <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
                <DialogTrigger asChild>
                  <button className="relative group">
                    {user?.photo_url ? (
                      <img 
                        src={user.photo_url} 
                        alt={user.name} 
                        className="w-10 h-10 rounded-full object-cover border-2 border-cyan-500/50"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                        <span className="text-white font-bold">{user?.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Update Profile Photo</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6">
                    <div className="flex flex-col items-center gap-4">
                      {photoPreview || user?.photo_url ? (
                        <img 
                          src={photoPreview || user?.photo_url} 
                          alt="Preview" 
                          className="w-32 h-32 rounded-full object-cover border-4 border-cyan-500/50"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-slate-700 flex items-center justify-center">
                          <User className="w-16 h-16 text-slate-400" />
                        </div>
                      )}
                      
                      <div className="flex flex-col items-center gap-2">
                        <label 
                          htmlFor="photo-upload-dashboard" 
                          className="cursor-pointer bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors"
                        >
                          <Camera className="w-4 h-4" />
                          Choose Photo
                        </label>
                        <input
                          id="photo-upload-dashboard"
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          className="hidden"
                        />
                        <p className="text-xs text-slate-400">JPG, PNG (Max 2MB)</p>
                      </div>
                    </div>
                    
                    {photoPreview && (
                      <Button 
                        onClick={handlePhotoUpload} 
                        disabled={uploading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white"
                      >
                        {uploading ? "Uploading..." : "Save Photo"}
                      </Button>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </header>

        <main className="p-6">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="team" element={<MyTeam />} />
            <Route path="income" element={<IncomeDetails />} />
            <Route path="wallet" element={<WalletSection />} />
            <Route path="orders" element={<MyOrders />} />
            <Route path="kyc" element={<KYCSection />} />
            <Route path="products" element={<ProductsView />} />
            <Route path="id-card" element={<MyIDCard />} />
            <Route path="security" element={<SecuritySection />} />
          </Routes>
        </main>
      </div>

      {/* Floating WhatsApp Support Button */}
      <a 
        href="https://wa.me/919956864243?text=Hi%20Achraj%20Support%2C%20I%20need%20help%20with%20my%20account"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all hover:scale-110"
        data-testid="whatsapp-support-btn"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
